// Plantilla del mensaje de turnos que envía el bot de WhatsApp.
// La usan el servidor (para enviar) y el panel (vista previa), así que no depende de Node ni del navegador.

export type TextStyle = 'normal' | 'bold' | 'italic';
export type MapMode = 'link' | 'button';

// Límites de la API de WhatsApp Cloud para mensajes interactivos cta_url
export const MAP_BUTTON_TEXT_MAX = 20;
export const PROMO_TEXT_MAX = 1000;

export interface TemplateLine {
  icon: string;
  style: TextStyle;
}

export interface BotMessageTemplate {
  title: TemplateLine & { text: string };
  date: TemplateLine & { text: string };
  name: TemplateLine & { uppercase: boolean };
  address: { style: TextStyle };
  phone: TemplateLine;
  // link: el link va en el texto. button: un mensaje por farmacia con botón que abre el mapa.
  map: { icon: string; mode: MapMode; buttonText: string };
  footer: TemplateLine & { text: string };
  // Publicidad de cierre: mensaje de texto aparte, después de las farmacias
  promo: { enabled: boolean; text: string };
}

export const DEFAULT_BOT_MESSAGE_TEMPLATE: BotMessageTemplate = {
  title: { icon: '📍', style: 'bold', text: 'FARMACIAS DE TURNO {DIA}' },
  date: { icon: '📅', style: 'normal', text: '{fecha} · de 8:30 a 8:30 del día siguiente' },
  name: { icon: '🏥', style: 'bold', uppercase: true },
  address: { style: 'normal' },
  phone: { icon: '📞', style: 'normal' },
  map: { icon: '🗺️', mode: 'link', buttonText: 'Ver en Google Maps' },
  footer: { icon: '', style: 'italic', text: '¡Compartilo con tus vecinos!' },
  promo: { enabled: false, text: '' },
};

export interface ShiftMessagePharmacy {
  nombre: string;
  direccion: string;
  telefono: string;
  mapUrl: string;
}

export interface ShiftMessageData {
  day: string;   // "hoy" o "mañana"
  date: string;  // ej. "15/09"
  pharmacies: ShiftMessagePharmacy[];
}

const TEXT_STYLES: TextStyle[] = ['normal', 'bold', 'italic'];

// Completa con los valores predeterminados lo que falte o esté mal guardado.
export function normalizeBotMessageTemplate(value: unknown): BotMessageTemplate {
  const d = DEFAULT_BOT_MESSAGE_TEMPLATE;
  const v: any = value && typeof value === 'object' ? value : {};
  const text = (x: unknown, fallback: string) => (typeof x === 'string' ? x : fallback);
  const style = (x: unknown, fallback: TextStyle) => (TEXT_STYLES.includes(x as TextStyle) ? (x as TextStyle) : fallback);

  return {
    title: { icon: text(v.title?.icon, d.title.icon), style: style(v.title?.style, d.title.style), text: text(v.title?.text, d.title.text) },
    date: { icon: text(v.date?.icon, d.date.icon), style: style(v.date?.style, d.date.style), text: text(v.date?.text, d.date.text) },
    name: {
      icon: text(v.name?.icon, d.name.icon),
      style: style(v.name?.style, d.name.style),
      uppercase: typeof v.name?.uppercase === 'boolean' ? v.name.uppercase : d.name.uppercase
    },
    address: { style: style(v.address?.style, d.address.style) },
    phone: { icon: text(v.phone?.icon, d.phone.icon), style: style(v.phone?.style, d.phone.style) },
    map: {
      icon: text(v.map?.icon, d.map.icon),
      mode: v.map?.mode === 'button' ? 'button' : 'link',
      buttonText: text(v.map?.buttonText, d.map.buttonText).trim().slice(0, MAP_BUTTON_TEXT_MAX) || d.map.buttonText
    },
    footer: { icon: text(v.footer?.icon, d.footer.icon), style: style(v.footer?.style, d.footer.style), text: text(v.footer?.text, d.footer.text) },
    promo: {
      enabled: v.promo?.enabled === true,
      text: text(v.promo?.text, d.promo.text).slice(0, PROMO_TEXT_MAX)
    },
  };
}

// WhatsApp no aplica el formato si el texto empieza o termina con espacios.
function applyStyle(value: string, style: TextStyle): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (style === 'bold') return `*${trimmed}*`;
  if (style === 'italic') return `_${trimmed}_`;
  return trimmed;
}

function withIcon(icon: string, content: string): string {
  if (!content) return '';
  return icon.trim() ? `${icon.trim()} ${content}` : content;
}

function renderParts(template: BotMessageTemplate, data: ShiftMessageData, includeMapLink: boolean) {
  const fill = (value: string) =>
    value.replace(/\{DIA\}/g, data.day.toUpperCase()).replace(/\{dia\}/g, data.day).replace(/\{fecha\}/g, data.date);

  const header = [
    withIcon(template.title.icon, applyStyle(fill(template.title.text), template.title.style)),
    withIcon(template.date.icon, applyStyle(fill(template.date.text), template.date.style)),
  ].filter(Boolean).join('\n');

  const pharmacyBlocks = data.pharmacies.map((pharm) => {
    const name = applyStyle(template.name.uppercase ? pharm.nombre.toUpperCase() : pharm.nombre, template.name.style);
    const address = applyStyle(pharm.direccion, template.address.style);
    return [
      withIcon(template.name.icon, address ? `${name} - ${address}` : name),
      withIcon(template.phone.icon, applyStyle(pharm.telefono, template.phone.style)),
      includeMapLink ? withIcon(template.map.icon, pharm.mapUrl) : '',
    ].filter(Boolean).join('\n');
  });

  const footer = withIcon(template.footer.icon, applyStyle(fill(template.footer.text), template.footer.style));

  return { header, pharmacyBlocks, footer };
}

// Mensaje único con los links al mapa en el texto (modo "link")
export function renderShiftMessage(template: BotMessageTemplate, data: ShiftMessageData): string {
  const { header, pharmacyBlocks, footer } = renderParts(template, data, true);
  return [header, ...pharmacyBlocks, footer].filter(Boolean).join('\n\n');
}

export interface ShiftMessageCard {
  body: string;
  buttonText: string;
  url: string;
}

// Modo "button": una tarjeta por farmacia (la API admite un solo botón de link por mensaje).
// El título va arriba de la primera tarjeta y la frase final debajo de la última.
export function renderShiftCards(template: BotMessageTemplate, data: ShiftMessageData): ShiftMessageCard[] {
  const { header, pharmacyBlocks, footer } = renderParts(template, data, false);
  const last = pharmacyBlocks.length - 1;
  return pharmacyBlocks.map((block, i) => ({
    body: [i === 0 ? header : '', block, i === last ? footer : ''].filter(Boolean).join('\n\n'),
    buttonText: template.map.buttonText,
    url: data.pharmacies[i].mapUrl,
  }));
}

// Texto de la publicidad de cierre, o null si no hay que mandarla
export function renderPromoMessage(template: BotMessageTemplate): string | null {
  const text = template.promo.text.trim();
  return template.promo.enabled && text ? text : null;
}
